import { useMemo, useState } from "react";
import {
  IconAlertTriangle,
  IconArrowDownLeft,
  IconArrowUpRight,
  IconBuildingBank,
  IconDeviceMobile,
  IconPlus,
  IconReceipt,
  IconRotate,
  IconShieldCheck,
  IconTrash,
  IconWallet,
} from "@tabler/icons-react";
import { api } from "../lib/api";
import type { CatalogEntry, Destination, WalletView } from "../lib/types";
import { formatDateTime, tzs } from "../lib/format";
import { useAuth } from "../lib/auth";
import { useI18n, type TKey } from "../i18n";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Chips,
  cx,
  EmptyState,
  ErrorAlert,
  Input,
  ListSkeleton,
  PageTitle,
  Sheet,
  Spinner,
  useAction,
  useLoad,
  useToast,
} from "../components/ui";

const ENTRY: Record<string, TKey> = { reward: "h_reward", reversal: "h_reversal", cashout: "h_cashout", forfeit: "h_forfeit" };

const DestIcon = ({ type }: { type: string }) => (type === "bank" ? <IconBuildingBank size={20} /> : <IconDeviceMobile size={20} />);

function DestinationCard({ d, selected, onSelect, onRemove }: { d: Destination; selected?: boolean; onSelect?: () => void; onRemove?: () => void }) {
  const { t } = useI18n();
  const body = (
    <>
      <span className={cx("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", selected ? "bg-primary text-white" : "bg-sunken text-ink")}>
        <DestIcon type={d.type} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-ink">{d.label}</span>
        <span className="flex items-center gap-1 truncate text-sm text-muted">
          {d.accountName}
          {d.isVerified && <IconShieldCheck size={14} className="shrink-0 text-success" aria-label={t("verifiedAccount")} />}
        </span>
        {d.nameMismatch && (
          <span className="mt-1 flex items-center gap-1 text-xs font-medium text-warning">
            <IconAlertTriangle size={13} /> {t("nameMismatch")}
          </span>
        )}
      </span>
    </>
  );
  if (onSelect)
    return (
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        onClick={onSelect}
        className={cx("flex w-full items-center gap-3 rounded-2xl border-2 p-3 text-left transition", selected ? "border-primary bg-primary-soft/50" : "border-line hover:border-line-strong")}
      >
        {body}
        <span className={cx("flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2", selected ? "border-primary bg-primary" : "border-line-strong")}>
          {selected && <span className="h-2 w-2 rounded-full bg-white" />}
        </span>
      </button>
    );
  return (
    <div className="flex items-center gap-3 py-3">
      {body}
      {onRemove && (
        <button type="button" onClick={onRemove} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted hover:bg-danger-soft hover:text-danger" aria-label={`${t("removeAccount")} ${d.label}`}>
          <IconTrash size={18} />
        </button>
      )}
    </div>
  );
}

/** Provider + number + name. Saving confirms the holder with Selcom where it can. */
function AddAccountForm({ onSaved, submitLabel }: { onSaved: (d: Destination) => void; submitLabel: string }) {
  const { t } = useI18n();
  const { me } = useAuth();
  const catalog = useLoad(() => api.get<CatalogEntry[]>("/agent/payout/catalog"));
  const [fiCode, setFiCode] = useState("");
  const [account, setAccount] = useState("");
  const [name, setName] = useState(me?.name ?? "");
  const entry = catalog.data?.find((c) => c.shortcode === fiCode);
  const numeric = entry?.referenceType === "NUMERIC";
  const save = useAction(async () => onSaved(await api.post<Destination>("/agent/payout/destinations", { fiCode, accountNumber: account.trim(), accountName: name.trim() || undefined })));
  if (catalog.loading) return <Spinner inline />;
  // Selcom Pesa is a wallet too.
  const groups = [
    { key: "mobile-money", label: t("mobileMoney"), cats: ["mobile-money", "selcom-pesa"] },
    { key: "bank", label: t("bank"), cats: ["bank"] },
  ];
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save.run();
      }}
    >
      <ErrorAlert error={catalog.error} onRetry={catalog.reload} />
      <div className="flex flex-col gap-1.5">
        <label htmlFor="provider" className="text-sm font-medium text-ink">
          {t("provider")}
        </label>
        <select id="provider" value={fiCode} onChange={(e) => setFiCode(e.target.value)} className="min-h-[48px] w-full appearance-none rounded-xl border border-line-strong bg-surface px-3.5 text-base text-ink outline-none focus:border-primary focus:ring-4 focus:ring-primary/15">
          <option value="">{t("choose")}</option>
          {groups.map((g) => {
            const items = (catalog.data ?? []).filter((c) => g.cats.includes(c.category));
            return items.length ? (
              <optgroup key={g.key} label={g.label}>
                {items.map((c) => (
                  <option key={c.shortcode} value={c.shortcode}>
                    {c.name}
                  </option>
                ))}
              </optgroup>
            ) : null;
          })}
          {(catalog.data ?? [])
            .filter((c) => !groups.some((g) => g.cats.includes(c.category)))
            .map((c) => (
              <option key={c.shortcode} value={c.shortcode}>
                {c.name}
              </option>
            ))}
        </select>
      </div>
      <Input
        label={entry?.category === "bank" ? t("bankAccountNumber") : t("accountNumber")}
        inputMode={numeric || entry?.category !== "bank" ? "numeric" : "text"}
        value={account}
        onChange={(e) => setAccount(e.target.value.replace(/\s/g, ""))}
        hint={entry?.category === "bank" ? undefined : t("mobileNumberHint")}
      />
      <Input label={t("accountName")} hint={t("nameMustMatch")} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
      <ErrorAlert error={save.error} />
      <Button type="submit" block loading={save.loading} disabled={!fiCode || account.trim().length < 5}>
        {submitLabel}
      </Button>
    </form>
  );
}

/** Spec §8: full balance only, a minimum, one at a time, Safiri pays the fee. */
function CashOutSheet({ open, onClose, wallet, onDone }: { open: boolean; onClose: () => void; wallet: WalletView; onDone: (msg: string) => void }) {
  const { t } = useI18n();
  const dests = useLoad(() => (open ? api.get<Destination[]>("/agent/payout/destinations") : Promise.resolve(null)), [open]);
  const [choice, setChoice] = useState<string>("");
  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const list = dests.data ?? [];
  const selected = list.find((d) => d._id === choice) ?? list[0];
  const showAdd = adding || (!dests.loading && list.length === 0);
  const send = useAction(async () => {
    const r = await api.post<{ amount: number }>("/agent/payout/cash-out", { destinationId: selected?._id });
    onDone(t("cashOutSent", { amount: tzs(r.amount) }));
  });
  const close = () => {
    setAdding(false);
    setConfirming(false);
    send.setError(null);
    onClose();
  };

  return (
    <Sheet
      open={open}
      onClose={close}
      title={confirming ? t("confirmTitle") : showAdd ? t("addAccount") : t("cashOutTitle")}
      footer={
        !showAdd &&
        selected &&
        (confirming ? (
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => setConfirming(false)} disabled={send.loading}>
              {t("back")}
            </Button>
            <Button className="flex-[1.6]" loading={send.loading} onClick={() => void send.run()}>
              {t("confirmCashOut", { amount: tzs(wallet.availableAmount) })}
            </Button>
          </div>
        ) : (
          <Button block size="lg" onClick={() => setConfirming(true)}>
            {t("continue")}
          </Button>
        ))
      }
    >
      {dests.loading && !dests.data ? (
        <Spinner inline />
      ) : showAdd ? (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">{t("addAccountBody")}</p>
          <AddAccountForm
            submitLabel={t("saveAccount")}
            onSaved={async (d) => {
              await dests.reload();
              setChoice(d._id);
              setAdding(false);
            }}
          />
          {list.length > 0 && (
            <Button variant="ghost" block onClick={() => setAdding(false)}>
              {t("useSavedAccount")}
            </Button>
          )}
        </div>
      ) : confirming && selected ? (
        <div className="flex flex-col gap-4">
          <div className="rounded-2xl bg-sunken p-5 text-center">
            <p className="text-sm text-muted">{t("youWillReceive")}</p>
            <p className="tabular mt-1 text-3xl font-semibold text-ink">{tzs(wallet.availableAmount)}</p>
            <p className="mt-1 text-xs text-success">{t("noFee")}</p>
          </div>
          <dl className="divide-y divide-line rounded-2xl border border-line text-sm">
            <div className="flex justify-between gap-3 p-3.5">
              <dt className="text-muted">{t("payTo")}</dt>
              <dd className="text-right font-medium text-ink">{selected.label}</dd>
            </div>
            <div className="flex justify-between gap-3 p-3.5">
              <dt className="text-muted">{t("accountName")}</dt>
              <dd className="text-right font-medium text-ink">{selected.accountName}</dd>
            </div>
          </dl>
          {selected.nameMismatch && <Alert tone="warning">{t("nameMismatchWarn")}</Alert>}
          <p className="text-xs text-subtle">{t("cashOutFull")}</p>
          <ErrorAlert error={send.error} />
        </div>
      ) : (
        <div className="flex flex-col gap-3" role="radiogroup" aria-label={t("payTo")}>
          <p className="text-sm text-muted">{t("chooseAccount")}</p>
          {list.map((d) => (
            <DestinationCard key={d._id} d={d} selected={selected?._id === d._id} onSelect={() => setChoice(d._id)} />
          ))}
          <button type="button" onClick={() => setAdding(true)} className="flex items-center gap-3 rounded-2xl border-2 border-dashed border-line-strong p-3 text-left text-sm font-medium text-brand hover:bg-sunken">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft">
              <IconPlus size={20} />
            </span>
            {t("addAccount")}
          </button>
          <ErrorAlert error={dests.error} onRetry={dests.reload} />
        </div>
      )}
    </Sheet>
  );
}

/** Saved accounts: add one ahead of time, or remove an old one. */
function PayoutAccounts() {
  const { t } = useI18n();
  const toast = useToast();
  const dests = useLoad(() => api.get<Destination[]>("/agent/payout/destinations"));
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Destination | null>(null);
  const remove = useAction(async (d: Destination) => {
    await api.del(`/agent/payout/destinations/${d._id}`);
    setRemoving(null);
    toast(t("accountRemoved"));
    await dests.reload();
  });
  return (
    <Card>
      <CardHeader
        title={t("payoutAccounts")}
        subtitle={t("payoutAccountsBody")}
        action={
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
            <IconPlus size={16} /> {t("add")}
          </Button>
        }
      />
      {dests.loading && !dests.data ? (
        <Spinner inline />
      ) : dests.error ? (
        <ErrorAlert error={dests.error} onRetry={dests.reload} />
      ) : !dests.data?.length ? (
        <p className="rounded-xl bg-sunken p-4 text-center text-sm text-muted">{t("noAccounts")}</p>
      ) : (
        <div className="-my-3 divide-y divide-line">
          {dests.data.map((d) => (
            <DestinationCard key={d._id} d={d} onRemove={() => setRemoving(d)} />
          ))}
        </div>
      )}

      <Sheet open={adding} onClose={() => setAdding(false)} title={t("addAccount")}>
        <p className="mb-4 text-sm text-muted">{t("addAccountBody")}</p>
        <AddAccountForm
          submitLabel={t("saveAccount")}
          onSaved={async () => {
            setAdding(false);
            toast(t("accountSaved"));
            await dests.reload();
          }}
        />
      </Sheet>

      <Sheet
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title={t("removeAccountTitle")}
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => setRemoving(null)}>
              {t("cancel")}
            </Button>
            <Button variant="danger" className="flex-1" loading={remove.loading} onClick={() => removing && void remove.run(removing)}>
              {t("removeAccount")}
            </Button>
          </div>
        }
      >
        {removing && (
          <>
            <p className="text-sm text-muted">{t("removeAccountBody")}</p>
            <div className="mt-3 rounded-2xl border border-line px-3">
              <DestinationCard d={removing} />
            </div>
            <div className="mt-3">
              <ErrorAlert error={remove.error} />
            </div>
          </>
        )}
      </Sheet>
    </Card>
  );
}

type HistoryFilter = "all" | "reward" | "cashout" | "other";

export function WalletPage() {
  const { t, pick, lang } = useI18n();
  const toast = useToast();
  const w = useLoad(() => api.get<WalletView>("/agent/wallet"));
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<HistoryFilter>("all");
  const history = useMemo(() => {
    const h = w.data?.history ?? [];
    if (filter === "all") return h;
    if (filter === "other") return h.filter((x) => x.type === "reversal" || x.type === "forfeit");
    return h.filter((x) => x.type === filter);
  }, [w.data, filter]);

  if (w.loading && !w.data)
    return (
      <>
        <PageTitle>{t("wallet")}</PageTitle>
        <ListSkeleton rows={4} />
      </>
    );
  if (w.error || !w.data)
    return (
      <>
        <PageTitle>{t("wallet")}</PageTitle>
        <ErrorAlert error={w.error} onRetry={w.reload} />
      </>
    );
  const d = w.data;
  const toMin = d.minimumAmount > 0 ? Math.min(100, (Math.max(0, d.balance) / d.minimumAmount) * 100) : 100;
  const counts = {
    reward: d.history.filter((x) => x.type === "reward").length,
    cashout: d.history.filter((x) => x.type === "cashout").length,
    other: d.history.filter((x) => x.type === "reversal" || x.type === "forfeit").length,
  };

  return (
    <>
      <PageTitle>{t("wallet")}</PageTitle>
      <div className="flex flex-col gap-5">
        <div className="relative overflow-hidden rounded-3xl bg-ink p-5 text-canvas shadow-raised sm:p-6 dark:bg-surface dark:text-ink dark:ring-1 dark:ring-line">
          <img src="/img/pattern.png" alt="" className="pointer-events-none absolute -bottom-24 -right-16 w-72 opacity-[0.07]" aria-hidden />
          <div className="relative">
            <p className="flex items-center gap-2 text-sm opacity-80">
              <IconWallet size={18} /> {t("available")}
            </p>
            <p className={cx("tabular mt-2 text-4xl font-semibold tracking-tight", d.balance < 0 && "text-[#FF8A80]")}>{tzs(d.balance)}</p>
            {d.balance < d.minimumAmount && d.balance >= 0 && (
              <div className="mt-4 max-w-sm">
                <div className="h-1.5 overflow-hidden rounded-full bg-white/15 dark:bg-sunken">
                  <div className="h-full rounded-full bg-[#6EE7A0]" style={{ width: `${toMin}%` }} />
                </div>
                <p className="mt-2 text-xs opacity-80">{t("toMinimum", { amount: tzs(d.remainingToMinimum), min: tzs(d.minimumAmount) })}</p>
              </div>
            )}
            {d.balance >= d.minimumAmount && <p className="mt-1 text-xs opacity-70">{t("minimum", { amount: tzs(d.minimumAmount) })}</p>}
            {d.eligible && (
              <button type="button" onClick={() => setOpen(true)} className="mt-5 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 font-semibold text-white transition hover:bg-primary-hover active:scale-[.98] sm:w-auto">
                <IconArrowUpRight size={20} /> {t("cashOut", { amount: tzs(d.availableAmount) })}
              </button>
            )}
          </div>
        </div>

        {!d.eligible && d.reasonText && <Alert tone={d.reason === "payout-in-progress" ? "info" : "warning"}>{pick(d.reasonText)}</Alert>}

        <PayoutAccounts />

        <section>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="font-semibold text-ink">{t("history")}</h2>
            <button type="button" onClick={() => void w.reload()} className="inline-flex items-center gap-1 text-sm font-medium text-muted hover:text-ink" disabled={w.loading}>
              <IconRotate size={16} className={w.loading ? "animate-spin" : ""} /> {t("refresh")}
            </button>
          </div>
          {d.history.length > 0 && (
            <div className="mb-3">
              <Chips
                label={t("history")}
                value={filter}
                onChange={setFilter}
                options={[
                  { value: "all", label: t("all"), count: d.history.length },
                  { value: "reward", label: t("filterRewards"), count: counts.reward },
                  { value: "cashout", label: t("filterCashouts"), count: counts.cashout },
                  ...(counts.other ? [{ value: "other" as const, label: t("filterOther"), count: counts.other }] : []),
                ]}
              />
            </div>
          )}
          {history.length === 0 ? (
            <EmptyState icon={IconReceipt} title={t("noHistory")} body={d.history.length ? undefined : t("noHistoryBody")} />
          ) : (
            <Card className="!p-0">
              <ul className="divide-y divide-line">
                {history.map((h) => {
                  const credit = h.signedAmount >= 0;
                  return (
                    <li key={h.id} className="flex items-start gap-3 p-4">
                      <span className={cx("flex h-10 w-10 shrink-0 items-center justify-center rounded-full", h.type === "reward" ? "bg-success-soft text-success" : h.type === "cashout" ? "bg-sunken text-ink" : "bg-danger-soft text-danger")}>
                        {h.type === "reward" ? <IconArrowDownLeft size={20} /> : h.type === "cashout" ? <IconArrowUpRight size={20} /> : <IconRotate size={20} />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-ink">{ENTRY[h.type] ? t(ENTRY[h.type]) : pick(h.title)}</p>
                        <p className="truncate text-sm text-muted">{[h.inviteeName, h.tier, h.destination, h.reason].filter(Boolean).join(" · ")}</p>
                        <p className="mt-0.5 text-xs text-subtle">{formatDateTime(h.createdAt, lang)}</p>
                        {h.receipt && (
                          <p className="mt-1 inline-flex items-center gap-1 rounded-md bg-sunken px-1.5 py-0.5 font-mono text-[11px] text-muted">
                            <IconReceipt size={12} /> {t("receiptRef")}: {h.receipt}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1.5">
                        <p className={cx("tabular text-sm font-semibold", credit ? "text-success" : "text-ink")}>
                          {credit ? "+" : "−"}
                          {tzs(Math.abs(h.signedAmount))}
                        </p>
                        {h.type === "cashout" && <Badge status={h.status === "COMPLETED" ? "paid" : h.status === "FAILED" ? "rejected" : "pending"}>{t(`co_${h.status}` as TKey)}</Badge>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
        </section>
      </div>

      {d.eligible && (
        <CashOutSheet
          open={open}
          onClose={() => setOpen(false)}
          wallet={d}
          onDone={(msg) => {
            setOpen(false);
            toast(msg);
            void w.reload();
          }}
        />
      )}
    </>
  );
}

